import React, { useCallback, useEffect } from 'react';
import {
  ButtonDiv,
  ButtonWrapper,
  Detail,
  Div,
  FeatureDiv,
  Features,
  Heading,
  HeadingSection,
  Icon,
  Icons,
  LeftDiv,
  ModalDiv,
  ProgressDiv,
  ProgressText,
  RightDiv,
  SubDiv,
  Title
} from './styles';
import Button from '../../button';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { getTotalTaskCount } from '../../../services/taskServices';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import { ACCOUNT_ANALYTICS } from '../../../global/analyticsConstants';
import ProgressBar from '../../progressBar';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';

declare global {
  interface Window {
    Intercom?: (command: string) => void;
  }
}

interface Props {
  onClickUpgrade: () => void;
  closeModel: () => void;
}

export default observer(function PlanUpgradeModal({ onClickUpgrade, closeModel }: Props) {
  //Other variable
  const primaryColor = 'var(--brand-primary)';
  // eslint-disable-next-line @typescript-eslint/no-unused-vars, no-unused-vars
  const totalTasksLimit = 250;

  //Mobx store variables
  const { workspaceStore } = rootStore;
  const { workspaceTotalTask, currentWorkspace } = workspaceStore;

  const loadData = useCallback(async () => {
    const data = await getTotalTaskCount();
    if (data) {
      const analyticsParams = {
        source: 'navbar',
        task_count: Number(data.totalTasks),
        workspace_name: currentWorkspace?.name
      };

      trackAnalyticActivity(ACCOUNT_ANALYTICS.SALES_UPGRADE_POPUP_DISPLAYED, analyticsParams);
    }
  }, [currentWorkspace?.name]);

  const onContactUsClick = useCallback(() => {
    if (window.Intercom && typeof window.Intercom === 'function') {
      window.Intercom('show');
    }
    closeModel();
    trackAnalyticActivity(`support: contact us clicked`);
  }, [closeModel]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  return (
    <>
      <ModalDiv>
        <Div>
          <LeftDiv>
            <Icon>
              <SVGIcon
                name='round-up-arrow-icon'
                width={ICON_SIZE.XXL}
                height={ICON_SIZE.XXL}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-white)'
              />
            </Icon>
            <HeadingSection>
              <Heading>Your workspace is on the free plan</Heading>
              <Detail>Upgrade to remove limits and access additional features</Detail>
            </HeadingSection>
            <ProgressDiv>
              <ProgressBar
                percent={
                  (Number(workspaceTotalTask) / totalTasksLimit) * 100 > 100
                    ? 100
                    : (Number(workspaceTotalTask) / totalTasksLimit) * 100
                }
                progressColor={primaryColor}
                showInfo={false}
                height={2}
              />
              <ProgressText>
                <span>{Number(workspaceTotalTask)}</span>/{totalTasksLimit} Tasks
              </ProgressText>
            </ProgressDiv>
          </LeftDiv>
          <RightDiv>
            <Title>Paid features</Title>
            <Features>
              <FeatureDiv>
                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='customer-icon'
                      width={ICON_SIZE.LG}
                      height={ICON_SIZE.LG}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      className='fill-color'
                    />
                  </Icons>
                  <p>Unlimited User</p>
                </SubDiv>
                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='project-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      className='fill-color'
                    />
                  </Icons>
                  <p>Unlimited Projects & Task</p>
                </SubDiv>
                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='milestone-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='none'
                      className='fill-color'
                    />
                  </Icons>
                  <p>Milestone</p>
                </SubDiv>
                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='help-center-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='none'
                      className='fill-color'
                    />
                  </Icons>
                  <p>Docs & Whiteboard</p>
                </SubDiv>
              </FeatureDiv>
              <FeatureDiv>
                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='navbar-Report-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='none'
                      className='fill-color'
                    />
                  </Icons>
                  <p>Advanced Reports</p>
                </SubDiv>

                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='navbar-client-portal-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      className='fill-color'
                    />
                  </Icons>
                  <p>Client Portal</p>
                </SubDiv>

                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='invoice-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      fill='none'
                      className='fill-color'
                    />
                  </Icons>
                  <p>Invoicing and Payment</p>
                </SubDiv>

                <SubDiv>
                  <Icons>
                    <SVGIcon
                      name='navbar-support-icon'
                      width={ICON_SIZE.MD}
                      height={ICON_SIZE.MD}
                      viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                      className='fill-color'
                    />
                  </Icons>
                  <p>Priority Support</p>
                </SubDiv>
              </FeatureDiv>
            </Features>
          </RightDiv>
        </Div>
        <ButtonDiv>
          <Button title='Contact Us' variant={BUTTON_TYPE.SECONDARY} size='small' onClick={onContactUsClick} />
          <ButtonWrapper>
            <Button title='Upgrade Now' variant={BUTTON_TYPE.PRIMARY} size='small' onClick={onClickUpgrade} />
          </ButtonWrapper>
        </ButtonDiv>
      </ModalDiv>
    </>
  );
});
